/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Formula } from '@gh/builder-shared-data';
import {
  composeEntityMergers,
  composeSetters,
  entityFieldMergeBy,
  entityFieldsMerger,
  EntityMerge,
  entityReplaceOrMergeWith,
  fieldValidationSetter,
  onChangeReducer,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { flatMap, isEqual, negate, uniq } from 'lodash';
import {
  complementJeItem,
  getJeCostCenterId,
  getJeItemId,
  getJeItemTopIds,
  isJeItemFieldAffectsPrice,
  isJeItemLocked,
  isJeSalesTaxItem,
  JeCostCenter,
  JeItem,
  jeQuantityResetSetter,
  validateJeItemField,
} from '../../../resources/job-estimate-details.model';
import {
  JeDetailsState,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  jeItemTable,
  queryJeAllModifiedCostCenters,
  queryJeCostCenterOrder0,
  queryJeItem,
} from '../je-details.state';
import { updateCostCenters } from './je-details-cc.reducers';
import { markComponentAsDirty } from './je-details-component.reducers';
import {
  cuttingListReducer,
  mergeCuttingList,
  rootCuttingListReducer,
} from './je-details-cutting-list.reducers';
import { selectionConsistencyReducer } from './je-details-selection.reducers';
import { addOrUpdateTops, getModifiedTops } from './je-details-top.reducers';

const mergeJeComponentOrFreeItem = entityReplaceOrMergeWith(composeEntityMergers(
  entityFieldsMerger<JeItem>(['quantity', 'extension']),
  entityFieldMergeBy<JeItem, 'cuttingList'>('cuttingList', mergeCuttingList)));

export const mergeJeItem: EntityMerge<JeItem> = (oldItem, newItem, id) =>
  oldItem && isJeSalesTaxItem(oldItem) ? oldItem : mergeJeComponentOrFreeItem(oldItem, newItem, id);

export const setCostCenterItemField = composeSetters(
  setEntityField,
  jeQuantityResetSetter,
  setUpdatedDirtyType,
  fieldValidationSetter(validateJeItemField));

export const jeDetailsCcItemReducer = onChangeReducer(
  itemsReducer,
  costCenterItemOnChangeReducer,
  componentItemOnChangeReducer,
  formulaItemOnChangeReducer,
  rootItemOnChangeReducer,
  toOnChangeReducer(selectionConsistencyReducer),
);

const findJeAllLockedItems = (items: Table<JeItem>) => jeItemTable.all(items).filter(isJeItemLocked);
const findJeAllUnlockedItems = (items: Table<JeItem>) => jeItemTable.all(items).filter(negate(isJeItemLocked));

function itemsReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterItemAddSet:
      return setEntityField(
        state,
        'jobItems',
        jeItemTable.insertAll(state.jobItems, event.itemSet.map(setNewDirtyType).map(complementJeItem)));
    case JeDetailsStateEventType.CostCenterItemChangeCc:
      return setEntityField(
        state,
        'jobItems',
        jeItemTable.mergeById(
          state.jobItems,
          event.itemId,
          (row: JeItem) => {
            const order = queryJeCostCenterOrder0(state, event.costCenterId);
            const itemWithCc = setCostCenterItemField(row, 'costCenterId', event.costCenterId);
            return order
              ? setCostCenterItemField(itemWithCc, 'order', order)
              : setCostCenterItemField(itemWithCc, 'order', void 0);
          }));
    case JeDetailsStateEventType.CostCenterItemSetField:
      return setEntityField(
        state,
        'jobItems',
        jeItemTable.mergeById(
          state.jobItems,
          event.itemId,
          (row: JeItem) => setCostCenterItemField(row, event.name, event.value)));
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemRemove:
    case JeDetailsStateEventType.CuttingListItemSetField:
      return setEntityField(
        state,
        'jobItems',
        jeItemTable.mergeById(
          state.jobItems,
          event.sourceId,
          (row: JeItem) => itemReducer(row, event)));
    case JeDetailsStateEventType.CostCenterItemRemove:
      return setEntityField(
        state,
        'jobItems',
        jeItemTable.mergeById(state.jobItems, event.itemId, removeDirtyRow));
    case JeDetailsStateEventType.LockAll:
      return setEntityField(
        state,
        'jobItems',
        jeItemTable.mergeAllById(
          state.jobItems,
          findJeAllUnlockedItems(state.jobItems).map(getJeItemId),
          (item) => setCostCenterItemField(item, 'isLocked', true)));
    case JeDetailsStateEventType.UnlockAll:
      return setEntityField(
        state,
        'jobItems',
        jeItemTable.mergeAllById(
          state.jobItems,
          findJeAllLockedItems(state.jobItems).map(getJeItemId),
          (item) => setCostCenterItemField(item, 'isLocked', false)));
    default:
      return state;
  }
}

function itemReducer(item: JeItem, event: JeDetailsStateEvent): JeItem {
  switch (event.type) {
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemRemove:
    case JeDetailsStateEventType.CuttingListItemSetField:
      return setCostCenterItemField(item, 'cuttingList', cuttingListReducer(item.cuttingList || [], event));
    default:
      return item;
  }
}

const updateDirtyType = (data: Maybe<JeCostCenter>) => data ? setUpdatedDirtyType(data) : void 0;

function costCenterItemOnChangeReducer(oldState: JeDetailsState,
                                       newState: JeDetailsState,
                                       event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterItemAddSet:
      return updateCostCenters(newState, [event.costCenterId], updateDirtyType);
    case JeDetailsStateEventType.CostCenterItemSetField:
      return updateCostCenters(newState, [getCostCenterItemId(newState, event.itemId)], updateDirtyType);
    case JeDetailsStateEventType.CostCenterItemChangeCc:
      return updateCostCenters(
        newState,
        [getCostCenterItemId(oldState, event.itemId), getCostCenterItemId(newState, event.itemId)],
        updateDirtyType);
    case JeDetailsStateEventType.CostCenterItemRemove:
      return updateCostCenters(
        newState,
        [getCostCenterItemId(oldState, event.itemId)],
        updateDirtyType);
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemSetField:
    case JeDetailsStateEventType.CuttingListItemRemove:
      return updateCostCenters(newState, [getCostCenterItemId(newState, event.sourceId)], updateDirtyType);
    case JeDetailsStateEventType.LockAll:
    case JeDetailsStateEventType.UnlockAll:
      return updateCostCenters(
        newState,
        queryJeAllModifiedCostCenters(oldState, newState).map(getJeCostCenterId),
        updateDirtyType);
    default:
      return newState;
  }
}

function componentItemOnChangeReducer(_: JeDetailsState,
                                      newState: JeDetailsState,
                                      event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterItemSetField: {
      // when we change item from component we have to mark corresponding component as dirty
      const item = jeItemTable.byId(newState.jobItems, event.itemId);
      return item && item.jobComponentId ? markComponentAsDirty(newState, item.jobComponentId) : newState;
    }
    default:
      return newState;
  }
}

function formulaItemOnChangeReducer(oldState: JeDetailsState,
                                    newState: JeDetailsState,
                                    event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterItemAddSet:
      return addOrUpdateTops(newState, uniq(flatMap(event.itemSet, getJeItemTopIds)));
    case JeDetailsStateEventType.CostCenterItemSetField: {
      if (event.name === 'formula') {
        const formula: Formula = event.value;
        const item = <JeItem>jeItemTable.byId(newState.jobItems, event.itemId);
        if (formula.valid && !isEqual(formula.takeOffParams, item.topIds)) {
          return addOrUpdateTops(
            setEntityField(
              newState,
              'jobItems',
              jeItemTable.update(newState.jobItems, { ...item, topIds: formula.takeOffParams })),
            getModifiedTops(getJeItemTopIds(item), formula.takeOffParams));
        }
      }
      return newState;
    }
    case JeDetailsStateEventType.CostCenterItemRemove: {
      const item = <JeItem>jeItemTable.byId(oldState.jobItems, event.itemId);
      return addOrUpdateTops(newState, getJeItemTopIds(item));
    }
    default:
      return newState;
  }
}

function rootItemOnChangeReducer(_: JeDetailsState,
                                 state: JeDetailsState,
                                 event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterItemAddSet:
    case JeDetailsStateEventType.CostCenterItemRemove:
    case JeDetailsStateEventType.CostCenterItemChangeCc:
      return setRecalculationNeeded(setDirty(state));
    case JeDetailsStateEventType.CostCenterItemSetField:
      if (isJeItemFieldAffectsPrice(event.name)) {
        return setRecalculationNeeded(setDirty(state));
      } else {
        return setDirty(state);
      }
    case JeDetailsStateEventType.LockAll:
    case JeDetailsStateEventType.UnlockAll:
      return setDirty(state);
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemSetField:
    case JeDetailsStateEventType.CuttingListItemRemove:
      return rootCuttingListReducer(state, event);
    default:
      return state;
  }
}

function getCostCenterItemId(state: JeDetailsState, itemId: ID): ID {
  return queryJeItem(state, itemId)!.costCenterId;
}
